/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * Page InquiryInfo types
 *
 * @author Julia Ignatieva
 * @author Ilya Baranchikov
 * @author Viacheslav Smalenski
 * @version 1.28.0
 * @since 1.15.0
 */

import { Expose } from 'class-transformer';

export const HIDDEN_VISIBILITY = 'HIDDEN';

export enum EActionType {
    ANSWER = 'ANSWER',
    COMMENT = 'COMMENT',
    QUESTION = 'QUESTION',
    QUESTION_OPEN = 'QUESTION_OPEN',
    REPORT_NOTE = 'REPORT_NOTE', // текст закрытия обращения
}

export enum EUserType {
    OPERATOR = 'OPERATOR',
    CLIENT = 'CLIENT',
    DEFAULT = 'DEFAULT',
}

export enum EInquiryChatNoteData {
    note = 'note',
}

export enum EInquiryStatusIds {
    WAITING_FOR_PROCESSING,
    IN_WORK,
    WAITING_FOR_CLIENT,
    FROZEN,
    WAITING_FOR_APPROVAL,
    CLOSED,
}

export interface IInquiryInfoGeneral {
    chat: IInquiryChat;
    hasInformationForUser: boolean;
    inquiry: IInquiryEntity;
    isInquiryCanBeClosed: boolean;
    isInquiryClosed: boolean;
    isShowAnswerBlock: boolean;
    isShowApproveBlock: boolean;
    isShowCloseInquiryButton: boolean;
    lastActionDate: string;
    noteMaxLength: number;
    waitLoad: boolean;
    allowedFileExtensions: string[];
    maxFileCount: number;
}

export interface IInquiryChat {
    hasMissingActions: boolean;
    lastActions: IInquiryChatAction[];
    missingActions: IInquiryChatAction[];
}

export interface IInquiryChatAction {
    date?: string;
    note: string;
    opName?: string;
    type: EActionType;
}

export interface IInquiryEntity {
    contact: IInquiryContact;
    createDate: string;
    description: string;
    files: IInquiryAttachedFile[];
    filesCount: number;
    formattedId: string;
    id: string;
    notifyMail: string;
    notifyPhone: string;
    planCloseDate: string;
    serviceType: string;
    state: IInquiryState;
    subscriber: IInquirySubscriber;
    topic: ITopic;
    updateDate: string;
    visibleInquiryAttributes?: IInquiryAttributeResponseData[];
}

export interface IInquiryAttributeResponseData {
    declaration: IDeclarationResponseData;
    id: string;
    key: string;
    name: string;
    valueList: IInquiryValueResponseData[];
}

export interface IInquiryValueResponseData {
    value: string;
}

export interface IDeclarationResponseData {
    dataType: EDataType;
    visibility: string;
}

export enum EDataType {
    STRING = 'STRING',
    DATE = 'DATE',
    NUMBER = 'NUMBER',
    BOOL = 'BOOL',
    DICTIONARY = 'DICTIONARY',
}

export interface IInquiryAttachedFile {
    name: string;
    id: number;
    createDate: string;
    createUser: string;
}

export interface ITopic {
    def: string;
    id: number;
}

export interface IInquiryState {
    dateStart: string;
    id: number;
    name: string;
    reportNote: string;
}

export interface IInquirySubscriber {
    id: number;
    msisdn: string;
}

export interface IInquiryContact {
    customer: IInquiryCustomer;
    subscriber: IInquirySubscriber;
}

export interface IInquiryCustomer {
    accountNumber: string;
    id: number;
    name: string;
}

export interface IInquiryInfoChatNewFile {
    id: number;
    name: string;
}

export class InquiryInfoNoteData {
    @Expose({ name: 'note' })
    note = '';
}
